import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, AppBar, Toolbar, IconButton, Typography, Button,
    Box, Stepper, Step, StepLabel, Grid, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch,
    Chip, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Divider, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, Alert, InputAdornment,
    CircularProgress
} from '@mui/material';
import {
    Close, NavigateNext, NavigateBefore, PlayArrow,
    Code, ContentCopy, ExpandMore, Key, Refresh,
    Category, Functions, Add, DeleteOutline, Search,
    ArrowDownward, ArrowUpward, AddCircleOutline
} from '@mui/icons-material';

import { useData } from '../../hooks/useData';
import { apiFetch } from '../../api';
import { LoadingSpinner } from '../Common';
import { DynamicIcon } from '../icons';
import { useNotification } from '../../context/NotificationContext';

const STEPS = ['Basics', 'Structure & Metrics', 'Filters', 'Preview'];

const STORAGE_KEY = 'dataset_builder_draft';

const OPERATORS = [
    { value: 'equals', label: '=' },
    { value: 'not_equals', label: '!=' },
    { value: 'greater', label: '>' },
    { value: 'less', label: '<' },
    { value: 'contains', label: 'LIKE' },
    { value: 'in', label: 'IN' },
    { value: 'is_null', label: 'IS NULL' }
];

const AGGREGATIONS = [
    { value: 'count', label: 'COUNT' },
    { value: 'sum', label: 'SUM' },
    { value: 'avg', label: 'AVG' },
    { value: 'min', label: 'MIN' },
    { value: 'max', label: 'MAX' },
    { value: 'count_distinct', label: 'COUNT DISTINCT' }
];

const DatasetBuilderDialog = ({ open, onClose, onSuccess, token, initialData }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [draftId, setDraftId] = useState(null);
    const { addNotification } = useNotification();

    // --- Search State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedTables, setExpandedTables] = useState({});

    const [config, setConfig] = useState({
        name: '',
        description: '',
        data_model_id: '',
        materialization_mode: 'live',
        generated_sql: '',
        query_config: {
            selected_columns: [],
            filters: [],
            // --- FIX: Default to null (No Limit) ---
            limit: null
        }
    });

    // --- Confirmation Dialog State ---
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingCustomToggle, setPendingCustomToggle] = useState(false);

    // --- Handlers ---
    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    // --- Load / Restore ---
    useEffect(() => {
        if (open) {
            // console.log('DatasetBuilderDialog initialData:', initialData);
            if (initialData) {
                setConfig({
                    name: initialData.name || '',
                    description: initialData.description || '',
                    data_model_id: initialData.data_model_id || '',
                    materialization_mode: initialData.materialization_mode || 'live',
                    generated_sql: initialData.generated_sql || '',
                    // Ensure boolean false (not undefined/null), handle 1/true
                    is_custom_sql: !!initialData.is_custom_sql,
                    // --- FIX: Use stored limit, defaulting to null ---
                    query_config: initialData.query_config || { selected_columns: [], filters: [], limit: null }
                });
                setDraftId(initialData.id);
                if (initialData.status === 'active') setActiveStep(3);
            } else {
                const savedDraft = localStorage.getItem(STORAGE_KEY);
                if (savedDraft) {
                    try {
                        const parsed = JSON.parse(savedDraft);
                        setConfig(parsed.config);
                        setActiveStep(parsed.activeStep || 0);
                    } catch (e) { }
                } else {
                    setDraftId(null);
                    setActiveStep(0);
                    // --- FIX: Reset to null ---
                    setConfig({
                        name: '', description: '', data_model_id: '', materialization_mode: 'live', generated_sql: '',
                        is_custom_sql: false, // Default to Auto
                        query_config: { selected_columns: [], filters: [], limit: null }
                    });
                }
            }
            setPreviewData(null); // Clear preview data
        }
    }, [open, initialData]);

    useEffect(() => {
        if (open && !initialData) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ config, activeStep }));
        }
    }, [config, activeStep, open, initialData]);

    const { data: dataModels } = useData('/datamodels', token);

    const selectedModel = useMemo(() => {
        if (!dataModels || !config.data_model_id) return null;
        return dataModels.find(m => m.id === config.data_model_id);
    }, [dataModels, config.data_model_id]);

    // --- Available Tables (Searchable) ---
    const availableTables = useMemo(() => {
        if (!selectedModel?.metadata_json?.extra?.visual_tables) return [];
        const allTables = selectedModel.metadata_json.extra.visual_tables;

        // Auto-expand
        if (Object.keys(expandedTables).length === 0 && allTables.length > 0) {
            setExpandedTables({ [allTables[0].name]: true });
        }

        if (!searchTerm) return allTables;

        // Filter tables/columns by search term
        return allTables.map(t => {
            const tableMatch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchingCols = t.columns.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

            if (tableMatch) return t; // Show whole table
            if (matchingCols.length > 0) return { ...t, columns: matchingCols }; // Show matching columns
            return null;
        }).filter(Boolean);
    }, [selectedModel, searchTerm]);

    // --- Buckets ---
    const dimensions = useMemo(() =>
        config.query_config.selected_columns.filter(c => !c.aggregation || c.aggregation === 'none'),
        [config.query_config.selected_columns]);

    const measures = useMemo(() =>
        config.query_config.selected_columns.filter(c => c.aggregation && c.aggregation !== 'none'),
        [config.query_config.selected_columns]);

    // --- Actions ---

    const addColumn = (tableName, colName, type) => {
        // type: 'dim' or 'metric'
        setConfig(prev => {
            // Prevent duplicates for Dimensions
            if (type === 'dim' && prev.query_config.selected_columns.find(c => c.table === tableName && c.field === colName && (!c.aggregation || c.aggregation === 'none'))) {
                return prev;
            }

            const newCol = {
                table: tableName,
                field: colName,
                aggregation: type === 'metric' ? 'count' : 'none'
            };

            return {
                ...prev,
                query_config: {
                    ...prev.query_config,
                    selected_columns: [...prev.query_config.selected_columns, newCol]
                }
            };
        });
    };

    // Select All: Toggles all columns in a table as Dimensions
    const handleToggleAllTable = (tableName, columns) => {
        setConfig(prev => {
            // Check if all are selected as dimensions
            const allSelected = columns.every(col =>
                prev.query_config.selected_columns.some(s => s.table === tableName && s.field === col.name && (!s.aggregation || s.aggregation === 'none'))
            );

            let newCols = [...prev.query_config.selected_columns];

            if (allSelected) {
                // Deselect all dimensions for this table
                newCols = newCols.filter(c => !(c.table === tableName && c.field && columns.some(tc => tc.name === c.field) && (!c.aggregation || c.aggregation === 'none')));
            } else {
                // Select missing
                columns.forEach(col => {
                    if (!newCols.find(c => c.table === tableName && c.field === col.name && (!c.aggregation || c.aggregation === 'none'))) {
                        newCols.push({ table: tableName, field: col.name, aggregation: 'none' });
                    }
                });
            }
            return { ...prev, query_config: { ...prev.query_config, selected_columns: newCols } };
        });
    };

    const updateColumnConfig = (oldCol, updates) => {
        setConfig(prev => ({
            ...prev,
            query_config: {
                ...prev.query_config,
                selected_columns: prev.query_config.selected_columns.map(c => c === oldCol ? { ...c, ...updates } : c)
            }
        }));
    };

    const removeColumnConfig = (colToRemove) => {
        setConfig(prev => ({
            ...prev,
            query_config: {
                ...prev.query_config,
                selected_columns: prev.query_config.selected_columns.filter(c => c !== colToRemove)
            }
        }));
    };

    // --- API Calls ---
    const preparePayload = () => {
        // Sanitize 'none' to null for backend
        const sanitizedCols = config.query_config.selected_columns.map(col => ({
            ...col,
            aggregation: (col.aggregation === 'none' || !col.aggregation) ? null : col.aggregation
        }));
        return {
            ...config,
            query_config: {
                ...config.query_config,
                // Ensure limit is parsed correctly or set to null
                limit: (config.query_config.limit === '' || config.query_config.limit === null) ? null : parseInt(config.query_config.limit),
                selected_columns: sanitizedCols
            },
            is_custom_sql: config.is_custom_sql
        };
    };

    const handleSaveDraft = async (silent = false) => {
        if (!config.name || !config.data_model_id) {
            if (!silent) addNotification("Name and Data Model are required.", "warning");
            return null;
        }
        if (!silent) setIsLoading(true);
        try {
            const payload = preparePayload();
            let result;
            if (draftId) {
                result = await apiFetch(`/datasets/${draftId}`, token, { method: 'PUT', body: JSON.stringify(payload) });
            } else {
                result = await apiFetch('/datasets', token, { method: 'POST', body: JSON.stringify(payload) });
                setDraftId(result.id);
            }
            if (result) setConfig(prev => ({ ...prev, generated_sql: result.generated_sql }));
            if (!silent) {
                localStorage.removeItem(STORAGE_KEY);
                onSuccess && onSuccess();
            }
            return result;
        } catch (err) {
            if (!silent) addNotification("Failed to save: " + err.message, "error");
            return null;
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    const handleRunPreview = async () => {
        setIsPreviewLoading(true);
        const saved = await handleSaveDraft(true);
        if (!saved) { setIsPreviewLoading(false); return; }
        try {
            const preview = await apiFetch(`/datasets/${saved.id}/preview?limit=10`, token);
            setPreviewData(preview);
        } catch (err) {
            addNotification("Preview failed: " + err.message, "error");
        } finally {
            setIsPreviewLoading(false);
        }
    };

    // --- Render Steps ---

    const renderStep1 = () => (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Basics</Typography>
            <Grid container spacing={3} maxWidth={800}>
                <Grid item xs={12}>
                    <TextField fullWidth label="Dataset Name" value={config.name} onChange={(e) => setConfig({ ...config, name: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                    <TextField fullWidth label="Description" multiline rows={2} value={config.description} onChange={(e) => setConfig({ ...config, description: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Source Data Model</InputLabel>
                        <Select value={config.data_model_id} label="Source Data Model" onChange={(e) => setConfig({ ...config, data_model_id: e.target.value })}>
                            {dataModels?.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Mode</InputLabel>
                        <Select value={config.materialization_mode} label="Mode" onChange={(e) => setConfig({ ...config, materialization_mode: e.target.value })}>
                            <MenuItem value="live">Live Query</MenuItem>
                            <MenuItem value="parquet">Cached Parquet (S3)</MenuItem>
                            <MenuItem value="cached_table">Cached Table (DB)</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* --- RESTORED ROW LIMIT INPUT --- */}
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Row Limit (Optional)"
                        type="number"
                        value={config.query_config.limit || ''}
                        onChange={(e) => setConfig({
                            ...config,
                            query_config: { ...config.query_config, limit: e.target.value }
                        })}
                        helperText="Leave empty for full dataset (No Limit). Set for testing."
                    />
                </Grid>
            </Grid>
        </Box>
    );

    const renderStep2 = () => (
        <Box sx={{ display: 'flex', height: '100%' }}>

            {/* PANE 1 (LEFT): Source Columns - ~38% of 65% = ~25% of Screen */}
            <Box sx={{ width: '38%', borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>1. Source Columns</Typography>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                            style: { fontSize: '0.8rem', height: 30 }
                        }}
                        fullWidth
                    />
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                    {availableTables.map((table) => (
                        <Accordion
                            key={table.name}
                            expanded={!!expandedTables[table.name] || !!searchTerm}
                            onChange={() => setExpandedTables(prev => ({ ...prev, [table.name]: !prev[table.name] }))}
                            disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' }, mb: 0.5 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 16 }} />} sx={{ minHeight: 32, px: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 1 }}>
                                    <Typography variant="caption" fontWeight="600" noWrap title={table.name} sx={{ maxWidth: 120 }}>{table.name}</Typography>
                                    <Box onClick={(e) => e.stopPropagation()}>
                                        <Button size="small" onClick={() => handleToggleAllTable(table.name, table.columns)} sx={{ fontSize: '0.6rem', minWidth: 'auto', p: 0, textTransform: 'none' }}>
                                            Select All
                                        </Button>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pl: 1 }}>
                                {table.columns.map((col) => {
                                    const isDim = config.query_config.selected_columns.some(s => s.table === table.name && s.field === col.name && (!s.aggregation || s.aggregation === 'none'));
                                    const isMetric = config.query_config.selected_columns.some(s => s.table === table.name && s.field === col.name && s.aggregation && s.aggregation !== 'none');

                                    return (
                                        <Box
                                            key={col.name}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderLeft: '2px solid #eee',
                                                '&:hover': { bgcolor: '#e0f2fe' },
                                                bgcolor: isDim ? '#f0f9ff' : isMetric ? '#fdf2f8' : 'transparent'
                                            }}
                                        >
                                            {col.isPrimaryKey ? <Key sx={{ fontSize: 12, color: 'orange' }} /> : <Box sx={{ width: 12 }} />}
                                            <Typography variant="caption" sx={{ flex: 1, fontWeight: (isDim || isMetric) ? 600 : 400 }} noWrap>{col.name}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', mr: 1 }}>{col.dataType.split('(')[0]}</Typography>

                                            {/* ADD ACTIONS */}
                                            <Tooltip title="Add to Dimensions">
                                                <IconButton size="small" onClick={() => addColumn(table.name, col.name, 'dim')} sx={{ color: 'primary.main', p: 0.2 }}>
                                                    <AddCircleOutline fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Add to Metrics">
                                                <IconButton size="small" onClick={() => addColumn(table.name, col.name, 'metric')} sx={{ color: 'secondary.main', p: 0.2 }}>
                                                    <Functions fontSize="inherit" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    );
                                })}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Box>

            {/* PANE 2 (MIDDLE): Design Query - ~62% of 65% = ~40% of Screen */}
            <Box sx={{ width: '62%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>

                {/* Top Half: Dimensions / Group By */}
                <Box sx={{ flex: 1, p: 2, overflowY: 'auto', borderBottom: '4px solid #f1f5f9' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', gap: 1, color: 'primary.main', mb: 1 }}>
                        <Category fontSize="small" /> 2. Dimensions / Group By
                    </Typography>

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell width="40%">Column</TableCell>
                                    <TableCell width="40%">Alias</TableCell>
                                    <TableCell width="20%" align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dimensions.map((col, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{col.table ? `${col.table}.${col.field}` : 'Empty Dim'}</TableCell>
                                        <TableCell>
                                            <TextField
                                                value={col.alias || ''}
                                                onChange={(e) => updateColumnConfig(col, { alias: e.target.value })}
                                                size="small" variant="standard" placeholder="Display Name" InputProps={{ disableUnderline: true }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Move to Metrics"><IconButton size="small" color="secondary" onClick={() => updateColumnConfig(col, { aggregation: 'count' })}><ArrowDownward fontSize="small" /></IconButton></Tooltip>
                                            <IconButton size="small" color="error" onClick={() => removeColumnConfig(col)}><DeleteOutline fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {dimensions.length === 0 && (
                                    <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.disabled', py: 2 }}>Select columns from left to group by.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {/* Bottom Half: Metrics */}
                <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', gap: 1, color: 'secondary.main', mb: 1 }}>
                        <Functions fontSize="small" /> 3. Metrics / Aggregations
                    </Typography>

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#fdf2f8' }}>
                                    <TableCell width="40%">Column</TableCell>
                                    <TableCell width="30%">Function</TableCell>
                                    <TableCell width="30%">Alias</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {measures.map((col, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell sx={{ fontSize: '0.8rem' }}>{col.table ? `${col.table}.${col.field}` : 'Empty Metric'}</TableCell>
                                        <TableCell>
                                            <Select
                                                value={col.aggregation}
                                                onChange={(e) => updateColumnConfig(col, { aggregation: e.target.value })}
                                                fullWidth size="small" variant="standard" disableUnderline
                                                sx={{ fontSize: '0.8rem', color: 'secondary.main', fontWeight: 600 }}
                                            >
                                                {AGGREGATIONS.map(agg => <MenuItem key={agg.value} value={agg.value}>{agg.label}</MenuItem>)}
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={col.alias || ''}
                                                onChange={(e) => updateColumnConfig(col, { alias: e.target.value })}
                                                size="small" variant="standard" placeholder="KPI Name" InputProps={{ disableUnderline: true }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Move to Dimension"><IconButton size="small" color="primary" onClick={() => updateColumnConfig(col, { aggregation: 'none' })}><ArrowUpward fontSize="small" /></IconButton></Tooltip>
                                            <IconButton size="small" color="error" onClick={() => removeColumnConfig(col)}><DeleteOutline fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {measures.length === 0 && (
                                    <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.disabled', py: 2 }}>No metrics defined.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Box>
    );

    const renderStep3 = () => (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Filters</Typography>
                <Button startIcon={<DynamicIcon name="Plus" />} onClick={() => setConfig(prev => ({ ...prev, query_config: { ...prev.query_config, filters: [...prev.query_config.filters, { table: '', column: '', operator: 'equals', value: '' }] } }))}>
                    Add Filter
                </Button>
            </Box>
            {config.query_config.filters.map((filter, idx) => (
                <Paper key={idx} sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Table</InputLabel>
                        <Select value={filter.table} label="Table" onChange={(e) => {
                            const newFilters = [...config.query_config.filters];
                            newFilters[idx].table = e.target.value;
                            setConfig(prev => ({ ...prev, query_config: { ...prev.query_config, filters: newFilters } }));
                        }}>
                            {availableTables.map(t => <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Column</InputLabel>
                        <Select value={filter.column} label="Column" onChange={(e) => {
                            const newFilters = [...config.query_config.filters];
                            newFilters[idx].column = e.target.value;
                            setConfig(prev => ({ ...prev, query_config: { ...prev.query_config, filters: newFilters } }));
                        }} disabled={!filter.table}>
                            {availableTables.find(t => t.name === filter.table)?.columns.map(c => <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Select size="small" value={filter.operator} onChange={(e) => {
                        const newFilters = [...config.query_config.filters];
                        newFilters[idx].operator = e.target.value;
                        setConfig(prev => ({ ...prev, query_config: { ...prev.query_config, filters: newFilters } }));
                    }}>
                        {OPERATORS.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                    </Select>
                    <TextField size="small" label="Value" value={filter.value} onChange={(e) => {
                        const newFilters = [...config.query_config.filters];
                        newFilters[idx].value = e.target.value;
                        setConfig(prev => ({ ...prev, query_config: { ...prev.query_config, filters: newFilters } }));
                    }} sx={{ flex: 1 }} />

                    <IconButton color="error" onClick={() => setConfig(prev => ({ ...prev, query_config: { ...prev.query_config, filters: prev.query_config.filters.filter((_, i) => i !== idx) } }))}>
                        <Close />
                    </IconButton>
                </Paper>
            ))}
        </Box>
    );

    const renderStep4 = () => (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Review & Finalize</Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
                Review the generated SQL in the right panel. Click "Run Preview" to verify data before saving.
            </Alert>
            <Button variant="contained" size="large" onClick={() => handleSaveDraft(false)} fullWidth sx={{ py: 2 }}>
                Save Dataset
            </Button>
        </Box>
    );

    // --- MAIN LAYOUT ---

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <AppBar position="relative" color="default" elevation={0} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={onClose}><Close /></IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        {initialData ? 'Edit Dataset' : 'Dataset Builder'}
                    </Typography>
                    <Button autoFocus variant="contained" onClick={() => handleSaveDraft(false)}>Save & Finish</Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ display: 'flex', flex: 1, height: '100%', overflow: 'hidden' }}>

                {/* LEFT CONTAINER (65%) */}
                <Box sx={{ width: '65%', display: 'flex', flexDirection: 'column', borderRight: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                        <Stepper activeStep={activeStep}>
                            {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                        </Stepper>
                    </Box>

                    <Divider />

                    <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default' }}>
                        {config.is_custom_sql && (
                            <Alert severity="warning" variant="filled" icon={false} sx={{ borderRadius: 0, py: 0.5, justifyContent: 'center' }}>
                                <strong>CUSTOM SQL ACTIVE</strong> &mdash; Builder controls below are ignored.
                            </Alert>
                        )}
                        {activeStep === 0 && renderStep1()}
                        {activeStep === 1 && renderStep2()}
                        {activeStep === 2 && renderStep3()}
                        {activeStep === 3 && renderStep4()}
                    </Box>

                    <Divider />

                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: 'background.paper' }}>
                        <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<NavigateBefore />}>Back</Button>
                        <Button
                            disabled={activeStep === STEPS.length - 1}
                            onClick={handleNext}
                            variant="contained"
                            endIcon={<NavigateNext />}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>

                {/* RIGHT CONTAINER (35%): Live Inspector */}
                <Box sx={{ width: '35%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
                    <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">LIVE INSPECTOR</Typography>

                        {/* --- CUSTOM SQL TOGGLE --- */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {config.is_custom_sql && <Chip size="small" label="ACTIVE" color="warning" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }} />}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Switch
                                    size="small"
                                    color="warning"
                                    checked={!!config.is_custom_sql}
                                    onChange={(e) => {
                                        const isCustom = e.target.checked;
                                        if (!isCustom) {
                                            // Open confirmation dialog
                                            setConfirmOpen(true);
                                        } else {
                                            setConfig(prev => ({ ...prev, is_custom_sql: true }));
                                        }
                                    }}
                                />
                                <Typography variant="caption" sx={{ cursor: 'pointer' }} onClick={() => setConfig(prev => ({ ...prev, is_custom_sql: !prev.is_custom_sql }))}>
                                    Custom SQL
                                </Typography>
                            </Box>

                            {!config.is_custom_sql && (
                                <Button size="small" startIcon={<Refresh />} onClick={() => handleSaveDraft(true)} sx={{ fontSize: '0.7rem' }}>
                                    Refresh
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* SQL Viewer */}
                    <Box sx={{ height: '35%', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', p: 0 }}>
                        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', bgcolor: 'action.selected' }}>
                            <Typography variant="caption" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: config.is_custom_sql ? 'warning.main' : 'text.primary' }}>
                                <Code fontSize="small" />
                                {config.is_custom_sql ? "Custom SQL (Editable)" : "Generated SQL (Read-Only)"}
                            </Typography>
                            <Tooltip title="Copy"><IconButton size="small" onClick={() => navigator.clipboard.writeText(config.generated_sql)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                        </Box>
                        <TextField
                            fullWidth
                            multiline
                            minRows={10} // Use minRows instead of fixed rows for better fill
                            maxRows={Infinity}
                            variant="standard"
                            InputProps={{ disableUnderline: true, style: { color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem', padding: '12px', height: '100%' } }}
                            value={config.generated_sql || "-- SQL will appear here"}
                            onChange={(e) => setConfig({ ...config, generated_sql: e.target.value })}
                            // Disable editing if NOT in custom mode
                            disabled={!config.is_custom_sql}
                            sx={{ flex: 1, overflow: 'auto', bgcolor: config.is_custom_sql ? 'background.default' : 'action.hover' }}
                        />
                    </Box>

                    {/* Data Preview */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" fontWeight="bold">Data Preview (Limit 10)</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={handleRunPreview}
                                disabled={isPreviewLoading}
                                startIcon={isPreviewLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                                sx={{ fontSize: '0.7rem', py: 0.5, minWidth: 80, minHeight: 28 }}
                            >
                                {isPreviewLoading ? 'Running' : 'Run'}
                            </Button>
                        </Box>

                        <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>
                            {previewData ? (
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {previewData.columns.map(c => (
                                                <TableCell key={c} sx={{ fontSize: '0.7rem', py: 1, px: 1, bgcolor: 'grey.100' }}>{c}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.rows.map((r, i) => (
                                            <TableRow key={i}>
                                                {previewData.columns.map((c, idx) => (
                                                    <TableCell key={c} sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
                                                        {Array.isArray(r) ? String(r[idx] ?? "") : String(r[c] ?? "")}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', flexDirection: 'column', gap: 1 }}>
                                    <PlayArrow fontSize="large" color="disabled" />
                                    <Typography variant="caption">Click Run to fetch data</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>

            </Box>

            {/* --- Confirmation Dialog --- */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <Box sx={{ p: 3, maxWidth: 400 }}>
                    <Typography variant="h6" gutterBottom>Disable Custom SQL?</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Switching back to Auto Mode will overwrite your custom SQL with the generated query. This action cannot be undone.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                        <Button variant="contained" color="warning" onClick={() => {
                            setConfig(prev => ({ ...prev, is_custom_sql: false }));
                            handleSaveDraft(true); // Trigger regeneration
                            setConfirmOpen(false);
                        }}>
                            Confirm & Overwrite
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Dialog >
    );
};

export default DatasetBuilderDialog;